import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowRight, Lock, User } from 'lucide-react';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { Button, ErrorMessage, Form, FormGroup, Input, Label } from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';
import { encodeToBase62, smartDecode } from '../utils/redirect';

const Message = styled.div`
  display: grid;
  gap: ${theme.spacing.md};
`;

const Hint = styled.div`
  color: ${theme.colors.textSecondary};
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
`;

const Field = styled.div`
  position: relative;
`;

const FieldIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  display: flex;
  align-items: center;
`;

const FieldInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

export const Login: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('redirect') || encodeToBase62('/');
  }, [location.search]);
  const redirectPath = useMemo(() => {
    try {
      return smartDecode(redirectParam);
    } catch {
      return '';
    }
  }, [redirectParam]);
  const shouldAutoStart = redirectPath.includes('portal=1');

  const retryLogin = () => {
    try {
      window.location.replace(authService.getOAuthLoginUrl(redirectParam));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '无法启动统一登录');
    }
  };

  const handlePasswordLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!username.trim() || !password) {
      setError('请输入账号和密码');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authService.login({
        username: username.trim(),
        password,
      });
      if (!response.success) {
        setError(response.error || response.message || '登录失败，请检查账号和密码');
        return;
      }

      window.location.replace(redirectPath || '/');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '登录失败，请稍后再试');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!shouldAutoStart) {
      return;
    }

    try {
      window.location.replace(authService.getOAuthLoginUrl(redirectParam));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '无法启动统一登录');
    }
  }, [shouldAutoStart, redirectParam]);

  return (
    <Layout
      title="统一登录"
      subtitle={shouldAutoStart ? '正在进入统一认证中心' : '请输入账号和密码'}
    >
      <Message>
        {shouldAutoStart ? (
          <>
            <Hint>
              <ArrowRight size={18} />
              <span>正在进入统一认证中心</span>
            </Hint>
            <Button type="button" $fullWidth onClick={retryLogin} disabled={shouldAutoStart}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                静默登录中
                <ArrowRight size={16} />
              </span>
            </Button>
          </>
        ) : (
          <Form onSubmit={handlePasswordLogin}>
            <FormGroup>
              <Label htmlFor="username">账号</Label>
              <Field>
                <FieldIcon>
                  <User size={18} />
                </FieldIcon>
                <FieldInput
                  id="username"
                  autoComplete="username"
                  placeholder="请输入账号或邮箱"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  $hasError={Boolean(error)}
                />
              </Field>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">密码</Label>
              <Field>
                <FieldIcon>
                  <Lock size={18} />
                </FieldIcon>
                <FieldInput
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="请输入密码"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  $hasError={Boolean(error)}
                />
              </Field>
            </FormGroup>

            {error && (
              <ErrorMessage>
                <AlertCircle size={16} />
                {error}
              </ErrorMessage>
            )}

            <Button type="submit" $fullWidth disabled={isSubmitting} $loading={isSubmitting}>
              {isSubmitting ? '登录中' : '登录'}
            </Button>
          </Form>
        )}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Link to="/forgot-password">忘记密码？</Link>
        </div>
      </Message>
    </Layout>
  );
};
